/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import ko from "knockout";
import birdata from "../api.js";
import _ from "underscore";
import moment from "dayjs";
import util from "../util/util.js";
import validate from "../util/validate.js";
import programs from "../programs.js";
import extensionsPromise from "../extensions.js";

import "./survey-edit-location.js";
import "./survey-edit-sightings.js";
import "./survey-edit-details.js";
import "./survey-edit-program.js";
import "./survey-edit-review.js";
import "./survey-edit-biby-site.js";
import "./survey-edit-biby-site-condition.js";
import "./survey-edit-site-details.js";

var DataEntryViewModel = function(params) {
	document.body.classList.add("birdata-fullscreen");

	var self = this;

	window.onbeforeunload = function(e) {
		if(!self.submitComplete() && initialState && getState() != initialState) {
			return "You haven't submitted your survey yet. Are you sure you wish to leave this page?";
		}
	};

	self.loaded = ko.observable(false);
	self.loadError = ko.observable(null);
	self.submitInProgress = ko.observable(false);
	self.submitComplete = ko.observable(false);
	self.surveyReferenceID = ko.observable();
	self.areaSurveyReferenceID = ko.observable();
	self.surveyShareURL = ko.observable();
	self.mePromise = birdata.me(); // See survey-edit-program.js

	var extensions;
	extensionsPromise().then(function(x) {
		extensions = x;
	});

	// Kick off an authenticated request to check that we have a logged-in user
	Promise.all([self.mePromise, extensionsPromise()]).then(function() {
		var params = util.parseParams(window.location.hash.substr(1));
		setupHistory();
		setupExtensions();

		// If necessary, load survey
		if(params.id) {
			return birdata.survey(params.id).then(loadSurvey)
		} else {
			return initNewSurvey();
		}
	}).then(function() {
		self.loaded(true);
		initState();
	}).catch(function(e) {
		self.loadError("Error loading survey");
		throw e;
	});

	var initialState;

	function initState() {
		setTimeout(function() {
			initialState = getState();
		}, 2000);
	}

	function initNewSurvey() {
		return programs().then(function(programs) {
			var defaultProgramCode = util.queryParams().program || 'birdata';
			var defaultProgram = _.findWhere(programs, { code: defaultProgramCode }) || _.findWhere(programs, { code: 'birdata' });
			self.selectProgram(defaultProgram);
		});
	}

	function getState() {
		return JSON.stringify(self.survey.toJSON());
	}

	function setupHistory() {
		window.addEventListener('hashchange', handleHashChange);
		handleHashChange();
	}

	function handleHashChange() {
		var section = _.findWhere(self.sections, {id: util.hashParams().tab}) || self.sections[0];
		if(!section.hidden()) {
			self.currentSection(section);
		}
	}

	var now = new Date();

	var survey = {
		startDate: ko.observable(now),
		startTimeHours: ko.observable(moment(now).format("HH")),
		startTimeMinutes: ko.observable(moment(now).format("mm")),
		finishDate: ko.observable(now),
		multipleDays: ko.observable(false),
		durationHours: ko.observable(''),
		durationMinutes: ko.observable(''),
		program: ko.observable(null),
		type: ko.observable(""),
		sightings: ko.observableArray([]),
		notes: ko.observable(""),
		observerCount: ko.observable(1),
		surveyPoint: ko.observable(null),
		routeInfo: ko.observable(null),
		sharedSite: ko.observable(null), // For site-based surveys e.g. egnb
		allSpeciesRecorded: ko.observable(true),
		existing: ko.observable(false),
		waterLevel: ko.observable(null),
		isPrivate: ko.observable(false),
		assignOwner: ko.observable(false),
		owner: ko.observable(null),
		// Used for programs where a pre-determined list of species should always be listed under the sighting tab (e.g. for BIBY)
		defaultSpecies: ko.observable([])
	};

	survey.editingModel = self;

	// This is an object where all program-specific state can be stored.
	// Each program must namespace its use of this object, e.g. survey.programData().s2020.xxxx
	// This will be populated based the program's data() callback (see selectSurvey)
	survey.programData = ko.observable({});
	survey.currentProgramData = ko.pureComputed(function() {
		return survey.programData()[survey.program().code];
	});

	// Object for storing extension-specific state
	survey.extensionData = {};

	function setupExtensions() {
		extensions.forEach(e => e.setupSurveyEditing(survey));
	}

	survey.coords = ko.pureComputed(function() {
		var sp = survey.surveyPoint();
		return sp ? (sp.lat + "," + sp.lon) : "";
	});

	window.surveyData = survey; // for debugging

	survey.uuid = util.uuid();

	survey.embeddedUuid = util.uuid();

	survey.isEmbedded = ko.pureComputed(function() {
		return survey.type() && survey.type().code == "2_ha_search+area_search_within_500_m";
	});

	survey.startTime = ko.pureComputed(function() {
		var h = +survey.startTimeHours();
		var m = +survey.startTimeMinutes();

		// Generate zero padded HH:mm
		var x = "" + (10000 + h * 100 + m);
		x = x.substr(1,2) + ":" + x.substr(3,2);
		return x;
	});

	survey.durationInMinutes = ko.pureComputed(function() {
		var h = +survey.durationHours();
		var m = +survey.durationMinutes();
		return h * 60 + m;
	});

	survey.validations = [
		validate(survey.startDate, function(date) {
			if(!moment(date).isValid()) {
				return {
					brief: "Invalid date",
					full: "Survey date is not a valid date (DD/MM/YYYY)"
				};
			} else if(moment(date).isAfter(moment())) {
				return {
					brief: "Date is in the future",
					full: "Survey date must not be in the future",
				};
			} else if(moment(date).isBefore(moment().subtract(100, 'years'))) {
				return {
					brief: "Date is too far in the past",
					full: "Survey date must be in the last 100 years"
				};
			}
		}),

		validate(survey.finishDate, function(date) {
			if(!survey.multipleDays()) return;
			var startDate = moment(survey.startDate());
			var finishDate = moment(date);

			if(!finishDate.isValid()) {
				return {
					brief: "Invalid date",
					full: "Finish date is not a valid date (DD/MM/YYYY)"
				};
			}

			if(startDate.isValid() && !finishDate.isAfter(startDate)) {
				return {
					brief: "Must be after start date",
					full: "Finish date must be after the start date"
				};
			}

			if(finishDate.isValid() && finishDate.isAfter(moment())) {
				return {
					brief: "Date is in the future",
					full: "Finish date must not be in the future",
				};
			}
		}),

		validate(survey.startTime, function() {
			var h = survey.startTimeHours();
			var m = survey.startTimeMinutes();

			if(h != '' && m != '' && h >= 0 && h <= 23 && m >= 0 && m <= 59) {
				return;
			} else {
				return {
					brief: "Time is not valid",
					full: "Start time is not valid"
				};
			}
		}),

		validate(survey.durationInMinutes, function(duration) {
			var type = survey.type() ? survey.type().code : null;
			if(!duration && type != 'incidental_search' && type != 'bird_list') {
				return "Survey duration is required";
			}

			if (duration && !(+duration > 0)) {
				return "Survey duration is invalid";
			}

			if(type == '2_ha_search' && duration != 20) {
				return "Survey duration must be 20 minutes for 2 ha, 20 minute search";
			}

			if(type == '5_min_point_search' && duration != 5) {
				return "Survey duration must be 5 minutes for 5 minute point search";
			}

			if(survey.isEmbedded() && duration < 20) {
				return "Survey duration must be at least 20 minutes for embedded surveys";
			}

			return survey.program()?.validateDuration?.(duration, survey);
		}),

		validate(survey.type, function(type) {
			if(!type) {
				return "Survey type is required";
			}
		}),

		validate(survey.observerCount, function(n) {
			var optional = survey.program()?.observerCountIsOptional?.(survey)
			if(util.isInt(n) && n == 0) {
				return "Number of observers must be at least 1";
			} else if(!optional && !n) {
				return "Number of observers is required";
			}
		}),

		validate(survey.owner, function(user) {
			if(survey.assignOwner() && !user) {
				return "Survey owner is required";
			}
		}),

		validate(survey.surveyPoint, function(surveyPoint) {
			if(!surveyPoint) {
				return "Survey location must be specified";
			}
		}, {tab: 'location'})
	];

	survey.dynamicValidations = ko.pureComputed(function() {
		var validations = [];

		var sp = survey.surveyPoint();

		// These validations only exist for a new survey point
		if(sp && !sp.id) {
			if(sp.name.error) validations.push(sp.name);
			if(sp.accuracy.error) validations.push(sp.accuracy);
		}

		var programData = survey.currentProgramData();
		if(programData && programData.validations) {
			validations = validations.concat(ko.unwrap(programData.validations));
		}

		validations = validations.concat(survey.routeValidations);

		return validations;
	});

	self.survey = survey;

	self.sections = [
		{
			title: "Program",
			component: "survey-edit-program",
			hidden: ko.pureComputed(() => survey.program()?.hideProgramTab ?? false),
			id: "program"
		},
		{
			title: "Location",
			component: "survey-edit-location",
			hidden: ko.pureComputed(function() { return survey.program() && survey.program().code == 'biby'; }),
			id: "location"
		},
		{
			title: "Location",
			component: "survey-edit-biby-site",
			hidden: ko.pureComputed(function() { return survey.program() && survey.program().code != 'biby'; }),
			id: "biby_site"
		},
		{
			title: "Site Details",
			component: "survey-edit-site-details",
			hidden: ko.pureComputed(function() { return !(survey.program() && survey.program().hasSiteDetailsTab) }),
			id: "site_details"
		},
		{
			title: "Details",
			component: "survey-edit-details",
			hidden: ko.observable(false),
			id: "details"
		},
		{
			title: "About My Site",
			component: "survey-edit-biby-site-condition",
			hidden: ko.pureComputed(function() { return survey.program() && survey.program().code != 'biby'; }),
			id: "biby_site_condition"
		},
		{
			title: "Sightings",
			component: "survey-edit-sightings",
			hidden: ko.observable(false),
			id: "sightings"
		},
		{
			title: "Review & Submit",
			component: "survey-edit-review",
			hidden: ko.observable(false),
			id: "review"
		}
	];

	self.currentSection = ko.observable(self.sections[0]);

	self.clickedTab = function(section) {
		if(!self.submitInProgress()) {
			if(self.currentSection() != section) {
				util.updateHashParams({tab : section.id});
			}

			self.currentSection(section);
		}
	};

	self.showNextTab = function() {
		var index = _.indexOf(self.sections, self.currentSection());
		while(index < self.sections.length - 1) {
			index++;
			var section = self.sections[index];
			if(!section.hidden()) {
				util.updateHashParams({tab : section.id});
				self.currentSection(section);
				break;
			}
		}
	};

	self.showTab = function(id) {
		var section = _.findWhere(self.sections, {id: id});
		if(section) {
			self.clickedTab(section);
		}
	};

	ko.computed(function() {
		if(self.currentSection().hidden()) {
			self.showTab('program'); // assuming that program tab is never hidden
		}
	});

	_.each(self.sections, function(section) {
		section.visible = ko.computed(function() {
			return self.currentSection() == section;
		});
	});

	// Scroll to top of page when switching between sections
	self.currentSection.subscribe(function() {
		window.scrollTo(0, 0);
	});

	// Pass self to all components in 'parent' parameter
	_.each(self.sections, function(section) {
		var component = section.component;
		if(typeof component == "string") {
			component = { name: component, params: {} };
			section.component = component;
		}
		component.params.parent = self;
	});

	survey.toJSON = function() {
		if(survey.isEmbedded()) {
			return { surveys: [ surveyJSON(false), surveyJSON(true) ] };
		} else {
			return surveyJSON(false);
		}
	};

	function surveyJSON(embedded) {
		var type;
		var durationInMinutes;

		if(survey.isEmbedded()) {
			type = embedded ? "area_search_within_500_m" : "2_ha_search";
		} else {
			type = survey.type() && survey.type().code;
		}

		if(survey.isEmbedded() && !embedded) {
			durationInMinutes = 20;
		} else {
			durationInMinutes = survey.durationInMinutes();
		}

		var surveyJSON = {
			start_date: moment(survey.startDate()).format("YYYY-MM-DD"),
			survey_type: type,
			program: survey.program().code,
			notes: survey.notes(),
			observer_count: +survey.observerCount() || null,
			uuid: embedded ? survey.embeddedUuid : survey.uuid,
			all_species_recorded: survey.allSpeciesRecorded(),
			is_private: survey.isPrivate()
		};

		if(survey.waterLevel()) {
			surveyJSON.water_level = survey.waterLevel().code;
		}

		if(durationInMinutes) {
			surveyJSON.duration_in_minutes = durationInMinutes;
		}

		if(survey.id) {
			surveyJSON.id = survey.id;
		}

		if(survey.multipleDays()) {
			surveyJSON.finish_date = moment(survey.finishDate()).format("YYYY-MM-DD");
		} else {
			surveyJSON.start_time = survey.startTime();
		}

		if(survey.program() && survey.program().recordSiteID && survey.sharedSite()) {
			surveyJSON.shared_site_id = survey.sharedSite().id;
		}

		surveyJSON.sightings = _.chain(survey.sightings())
			.filter(function(sighting) {
				return sighting.isPresent();
			}).map(function(sighting) {
				return sighting.toJSON(embedded);
			}).filter(function(sighting) {
				return sighting.count != null;
			}).value();

		var surveyPoint = survey.surveyPoint();
		if(surveyPoint) {
			if(surveyPoint.id) {
				surveyJSON.survey_point = {
					id: surveyPoint.id
				};
			} else {
				surveyJSON.survey_point = {
					lat: surveyPoint.lat,
					lon: surveyPoint.lon,
					name: surveyPoint.name()
				};
				if(surveyPoint.accuracy()) {
					surveyJSON.survey_point.accuracy_in_metres = surveyPoint.accuracy().metres;
				}
			}
		}

		if(survey.program().updateSurveyJSON) {
			survey.program().updateSurveyJSON(surveyJSON, survey);
		}

		extensions.forEach(e => e.updateSurveyJSON(surveyJSON, survey));

		if(survey.assignOwner() && survey.owner()) {
			if(survey.owner().id) {
				surveyJSON.owner = { id: survey.owner().id };
			} else {
				surveyJSON.owner = { full_name: survey.owner().full_name };
			}
		}

		return surveyJSON;
	}

	self.selectProgram = function(program) {
		var oldProgram = survey.program();

		if(program.beforeSelect) {
			program.beforeSelect(survey);
		}

		if(oldProgram && oldProgram.beforeDeselect) {
			oldProgram.beforeDeselect(survey);
		}

		// Set programData if this is first time program has been selected.
		var data = survey.programData();
		if(!data[program.code]) {
			data[program.code] = program.programData ? program.programData(survey) : {};
			survey.programData(data);
		}

		survey.program(program);

		// Allow program to update survey after being selected
		if(program.afterSelect) {
			program.afterSelect(survey);
		}

		if(oldProgram && oldProgram.afterDeselect) {
			oldProgram.afterDeselect(survey);
		}
	};

	function loadSurvey(j) { // j = survey JSON loaded from server

		// A bit messy, but this lets child components detect when we are loading an existing survey
		// and take appropriate action.
		self.loadedSurveyJSON = j;

		return Promise.all([birdata.lookup(), programs()]).then(function([lookups, programs]) {
			survey.existing(true);
			survey.id = j.id;
			survey.startDate(moment(j.start_date).toDate());
			if(j.duration_in_minutes) {
				survey.durationHours(Math.floor(j.duration_in_minutes / 60));
				survey.durationMinutes(j.duration_in_minutes % 60);
			}
			var type = lookups.find('survey_type', j.survey_type);
			survey.type(type);
			survey.allSpeciesRecorded(!!j.all_species_recorded);
			survey.waterLevel(lookups.find('water_level', j.water_level));

			survey.notes(j.notes);
			survey.observerCount(j.observer_count || null);
			survey.uuid = j.uuid;
			survey.multipleDays(!!j.finish_date);
			if(j.finish_date) survey.finishDate(moment(j.finish_date).toDate());
			if(j.start_time) {
				var hhmm = j.start_time.split(":");

				survey.startTimeHours(hhmm[0]);
				survey.startTimeMinutes(hhmm[1]);
			}
			survey.isPrivate(!!j.is_private);

			var program = programs.find(j.program || "none");
			if(program) {
				self.selectProgram(program);
				var fields = survey.currentProgramData().fields;
				if(program.loadFromJSON) {
					program.loadFromJSON(j, fields, survey);
				}
			}

			extensions.forEach(extension => {
				extension.loadFromJSON(j, survey.extensionData[extension.code].fields(), survey);
			});

			// Note: Survey point is handled by survey-edit-location child component
			// Note: Sightings are handled by survey-edit-sightings child component
		});
	}
	self.loadSurvey = loadSurvey; // export this so we can use it in survey-summary

	self.recordAnotherSurveyURL = ko.pureComputed(function() {
		var program = self.survey.program();

		if(program && program.recordAnotherSurveyURL) {
			return program.recordAnotherSurveyURL();
		} else {
			return 'record-survey';
		}
	});

	self.programClass = ko.pureComputed(() => {
		let programCode = self.survey.program()?.code || 'birdata';
		return 'program-' + programCode;
	});
};

ko.components.register('survey-edit', {
	viewModel: DataEntryViewModel,
	template: require('../../templates/survey-edit.html')
});

export default DataEntryViewModel;
